import { lazy } from "react"
import { useFragment } from "react-relay"
import { CountryIcon } from "@/components/country-icon.tsx"
import { ageFormat } from "@/lib/age-format"
import { cn } from "@/lib/utils"
import { GenderIcon } from "@/modules/patients/components/gender-icon.tsx"
import { CommunicationChannelIndicator } from "@/modules/patients/components/header/communication-channel-indicator"
import { PatientAddressTooltip } from "@/modules/patients/components/patient-info/patient-address-tooltip"
import { PatientInfo, PatientInfoHeader } from "@/modules/patients/components/patient-info/patient-info"
import { PatientContextAppointmentFragment$key } from "@/modules/patients/services/__generated__/PatientContextAppointmentFragment.graphql"
import { PatientFragment$data } from "@/modules/patients/services/__generated__/PatientFragment.graphql"
import { PatientContextAppointmentFragment } from "@/modules/patients/services/PatientContextAppointmentFragment"
import { ContactChannel } from "@/types/__generated__/union-types"

const ChCoverageTag = lazy(() =>
  import("@/modules/common/insurees/coverage-tag/ch/coverage-tag").then((m) => ({ default: m.ChCoverageTag })),
)
const DeCoverageTag = lazy(() =>
  import("@/modules/common/insurees/coverage-tag/de/coverage-tag").then((m) => ({ default: m.DeCoverageTag })),
)

export interface PatientInfoProps {
  patient: NonNullable<PatientFragment$data>
  contactChannel: ContactChannel
  patientId: string
  contextAppointmentKey: PatientContextAppointmentFragment$key | null
  wrapperClassName?: React.HTMLAttributes<HTMLDivElement>["className"]
}

const PatientInfoForMedicalRecord = ({
  patient,
  children,
  contextAppointmentKey,
  className,
  wrapperClassName,
  contactChannel,
  patientId,
}: PatientInfoProps & React.HTMLAttributes<HTMLDivElement>) => {
  const {
    address: patientAddress,
    firstName,
    lastName,
    gender,
    birthDate,
    age,
    country,
    primaryLanguage,
    secondaryLanguages,
    displayId,
  } = patient

  const patientContextAppointment = useFragment<PatientContextAppointmentFragment$key>(
    PatientContextAppointmentFragment,
    contextAppointmentKey,
  )

  const displayAge = ageFormat({ age })

  return (
    <div className={cn("group relative flex h-12 shrink grow items-center justify-start gap-4", wrapperClassName)}>
      <GenderIcon
        size={30}
        className={cn(
          "@sm:absolute @sm:top-0.5 @sm:left-0 @sm:size-6",
          "@xl:relative! @xl:top-auto! @xl:left-auto! @xl:size-10!",
        )}
        gender={gender}
      />
      <div className="flex flex-col">
        <PatientInfoHeader lastName={lastName} firstName={firstName}>
          {children}
        </PatientInfoHeader>
        <div className={cn("text-on-surface-subdued flex gap-1 text-sm whitespace-nowrap", className)}>
          <PatientInfo
            birthDate={birthDate}
            displayAge={displayAge}
            primaryLanguage={primaryLanguage}
            secondaryLanguages={secondaryLanguages}
            displayId={displayId}
          />
          <PatientAddressTooltip
            addressKey={patientContextAppointment?.patientAddress}
            patientAddress={patientAddress}
          />
          <CountryIcon size={20} country={country} data-testid="patient-country" />
          {country === "DE"
            ? patientId && <DeCoverageTag mode={{ kind: "SAVED", patientId }} />
            : patientId && <ChCoverageTag mode={{ kind: "SAVED", patientId }} />}
          {contactChannel && (
            <CommunicationChannelIndicator
              contactChannel={contactChannel}
              isContactChannelVerified={patient.isContactChannelVerified}
            />
          )}
        </div>
      </div>
    </div>
  )
}

export { PatientInfoForMedicalRecord }
